import React, { useState, useEffect } from 'react';
import { DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { apiService } from '../../services/api';
import { Conversion } from '../../types';
import ApiKeyFilter, { API_SCOPE } from '../filters/ApiKeyFilter';
import { idsParam } from '../filters/EntityFilter';
import { EmptyRow, SectionFilterBar, SortableTh, SortOrder, TableCard, TableHeader, TableViewport, usePageSize, tdClass } from '../table/TableKit';
import RelativeTime from '../RelativeTime';
import { ErrorNotice } from '../ErrorNotice';
import { formatDurationSeconds } from '../../utils/dateTime';
import { urlEnum, urlIdList, urlPositiveInt, useUrlState } from '../../hooks/useUrlState';
import { usePageClamp } from '../../hooks/usePageClamp';
import { apiKeyName } from '../../utils/apiKey';
import { formatCentsAsDollars } from '../../utils/money';
import CopyIconButton from '../CopyIconButton';

const URL_SPEC = {
    keys: urlIdList(),
    sort: urlEnum(['api_key', 'status', 'started_at', 'duration', 'input_tokens_converted', 'cost'] as const, 'started_at'),
    order: urlEnum(['asc', 'desc'] as const, 'desc'),
    page: urlPositiveInt(),
};

function ConvertLogTab({ urlPrefix }: { urlPrefix?: string }) {
    const [conversions, setConversions] = useState<Conversion[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [state, setUrlState] = useUrlState(URL_SPEC, urlPrefix);
    const [pageSize, setPageSize] = usePageSize('requests');
    const onSort = (sort: string, order: SortOrder) =>
        setUrlState({ sort: sort as typeof state.sort, order, page: 1 });
    const [logId, setLogId] = useState<string | null>(null);
    const [logText, setLogText] = useState('');
    const [logLoading, setLogLoading] = useState(false);
    const [logError, setLogError] = useState<string | null>(null);
    // Cost is for saved instances only, like the converter dashboard.
    const showCost = apiService.getInstanceInfo()?.instanceMode === 'persistent';
    const columnCount = showCost ? 6 : 5;

    useEffect(() => {
        // Dropped on the next filter change, so a slow old response cannot win.
        let current = true;
        const fetchConversions = async () => {
            setError(false);
            setLoading(true);
            try {
                const res = await apiService.converterGetConversions({
                    api_key_ids: idsParam(state.keys),
                    limit: pageSize,
                    offset: (state.page - 1) * pageSize,
                    sort: state.sort,
                    order: state.order,
                });
                if (!current) return;
                setConversions(res.data.conversions || []);
                setTotal(res.data.total || 0);
            } catch {
                if (!current) return;
                setConversions([]);
                setTotal(0);
                setError(true);
            } finally {
                if (current) setLoading(false);
            }
        };
        fetchConversions();
        return () => {
            current = false;
        };
    }, [pageSize, state.keys, state.order, state.page, state.sort]);

    usePageClamp({
        page: state.page,
        pageSize,
        total,
        ready: !loading && !error,
        setPage: (page) => setUrlState({ page }),
    });

    useEffect(() => {
        if (logId === null) return;
        // Dropped when the modal closes or another log opens, so a slow old response cannot win.
        let current = true;
        const fetchLog = async () => {
            setLogText('');
            setLogError(null);
            setLogLoading(true);
            try {
                const text = await apiService.converterGetLog(logId);
                if (!current) return;
                setLogText(text);
            } catch {
                if (!current) return;
                setLogError('Failed to load log.');
            } finally {
                if (current) setLogLoading(false);
            }
        };
        fetchLog();
        return () => {
            current = false;
        };
    }, [logId]);

    // Clear on close so a reopen never flashes the previous log or error.
    const closeLog = () => {
        setLogId(null);
        setLogText('');
        setLogError(null);
    };

    return (
        <div>
            {error && <ErrorNotice message="Failed to load conversions." />}
            <SectionFilterBar>
                <ApiKeyFilter scope={API_SCOPE} value={state.keys} onChange={(keys) => setUrlState({ keys, page: 1 })} className="h-10 text-base" />
            </SectionFilterBar>
            <TableCard>
                <TableHeader
                    title="Requests"
                    pagination={{
                        offset: (state.page - 1) * pageSize,
                        limit: pageSize,
                        total,
                        onOffsetChange: (offset) => setUrlState({ page: offset / pageSize + 1 }),
                        onLimitChange: (size) => {
                            setPageSize(size);
                            setUrlState({ page: 1 });
                        },
                    }}
                />
                <TableViewport className={loading ? "opacity-50 transition-opacity" : ""}>
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-700">
                                <SortableTh label="API Key" sortKey="api_key" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                <SortableTh label="Status" sortKey="status" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                <SortableTh label="Started At" sortKey="started_at" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="Duration" sortKey="duration" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="Converted Tokens" sortKey="input_tokens_converted" sort={state.sort} order={state.order} onSort={onSort} />
                                {showCost && (
                                    <SortableTh label="Cost" sortKey="cost" sort={state.sort} order={state.order} onSort={onSort} />
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {conversions.map((conv) => (
                                <React.Fragment key={conv.id}>
                                    <tr
                                        className="cursor-pointer hover:bg-body-bg/50"
                                        onClick={() => setExpandedId(expandedId === conv.id ? null : conv.id)}
                                    >
                                        <td className={tdClass}>{apiKeyName(conv)}</td>
                                        <td className={tdClass}>
                                            {conv.status === 'success' ? (
                                                <span className="bg-primary/20 text-primary px-2 py-1 rounded text-xs font-medium">success</span>
                                            ) : (
                                                <span className="inline-flex items-center gap-2">
                                                    <span className="bg-red-600/20 text-red-400 px-2 py-1 rounded text-xs font-medium">error</span>
                                                    <button
                                                        type="button"
                                                        title="View log"
                                                        aria-label="View log"
                                                        className="btn-icon p-1 text-secondary hover:text-primary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setLogLoading(true);
                                                            setLogId(conv.id);
                                                        }}
                                                    >
                                                        <DocumentTextIcon className="w-4 h-4" />
                                                    </button>
                                                </span>
                                            )}
                                        </td>
                                        <td className={`${tdClass} whitespace-nowrap`}>
                                            <RelativeTime value={conv.started_at} />
                                        </td>
                                        <td className={tdClass}>
                                            {formatDurationSeconds(conv.started_at, conv.completed_at)}
                                        </td>
                                        <td className={tdClass}>{conv.input_tokens_converted?.toLocaleString() ?? '—'}</td>
                                        {showCost && <td className={tdClass}>{formatCentsAsDollars(conv.cost_cents)}</td>}
                                    </tr>
                                    {expandedId === conv.id && (
                                        <tr>
                                            <td colSpan={columnCount} className="px-4 py-3">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <div className="flex items-center gap-1 text-sm font-medium text-secondary mb-1">
                                                            Source Code
                                                            <CopyIconButton text={conv.source_code} label="source code" />
                                                        </div>
                                                        <pre className="font-mono bg-body-bg p-4 rounded max-h-64 overflow-auto text-sm text-body-color">
                                                            {conv.source_code}
                                                        </pre>
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-1 text-sm font-medium text-secondary mb-1">
                                                            Converted Code
                                                            {conv.converted_code && <CopyIconButton text={conv.converted_code} label="converted code" />}
                                                        </div>
                                                        <pre className="font-mono bg-body-bg p-4 rounded max-h-64 overflow-auto text-sm text-body-color">
                                                            {conv.converted_code || conv.error_message || 'N/A'}
                                                        </pre>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            {conversions.length === 0 && (
                                <EmptyRow colSpan={columnCount} text={loading ? 'Loading...' : 'No conversions found.'} />
                            )}
                        </tbody>
                    </table>
                </TableViewport>
            </TableCard>
            {logId !== null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={closeLog}>
                    <div className="bg-widget-bg border border-secondary rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col mx-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary">
                            <h3 className="text-lg font-semibold">Convert Log</h3>
                            <button
                                onClick={closeLog}
                                aria-label="Close"
                                className="text-secondary hover:text-body-color transition-colors"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-4">
                            {logLoading && <div className="text-sm text-secondary text-center py-8">Loading...</div>}
                            {logError && <div className="text-sm text-red-400 text-center py-8">{logError}</div>}
                            {!logLoading && !logError && (
                                <pre className="text-sm font-mono text-body-color whitespace-pre-wrap break-words bg-body-bg p-4 rounded">
                                    {logText}
                                </pre>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ConvertLogTab;
